/**
 * Maps an Ant `Table` onChange (pagination, column filters, sorter) onto a list query patch.
 * Column `key`s are the query's field names: a sortable column's key is its `sort` value,
 * a filterable column's key is its filter param. Structural types keep this free of antd imports.
 */

type TableKey = string | number | bigint;

interface ColumnSorter {
  columnKey?: TableKey;
  order?: 'ascend' | 'descend' | null;
}

export interface TableChange {
  action: 'paginate' | 'sort' | 'filter';
  pagination: { current?: number; pageSize?: number };
  filters: Record<string, (TableKey | boolean)[] | null>;
  sorter: ColumnSorter | ColumnSorter[];
}

interface ListPosition {
  pageSize: number;
  defaultSort: { sort: string; order: 'asc' | 'desc' };
}

export type ListPatch = Record<string, unknown>;

function paginationPatch({ current, pageSize }: TableChange['pagination'], position: ListPosition): ListPatch {
  // A new page size would leave the old page number pointing somewhere arbitrary, so it starts over.
  if (pageSize && pageSize !== position.pageSize) return { page: 1, pageSize };
  return { page: current ?? 1 };
}

/** Ant cycles a column ascend → descend → cleared; cleared goes back to the screen's default sort. */
function sortPatch(sorter: TableChange['sorter'], position: ListPosition): ListPatch {
  const active = Array.isArray(sorter) ? sorter[0] : sorter;
  if (!active?.order || active.columnKey === undefined) return { page: 1, ...position.defaultSort };
  return { page: 1, sort: String(active.columnKey), order: active.order === 'descend' ? 'desc' : 'asc' };
}

function filterPatch(filters: TableChange['filters']): ListPatch {
  const patch: ListPatch = { page: 1 };
  for (const [key, values] of Object.entries(filters)) patch[key] = values?.length ? values.map(String) : undefined;
  return patch;
}

export function tableChangeToListPatch(change: TableChange, position: ListPosition): ListPatch {
  if (change.action === 'paginate') return paginationPatch(change.pagination, position);
  if (change.action === 'sort') return sortPatch(change.sorter, position);
  return filterPatch(change.filters);
}
